// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  addSingleFavourite,
  removeFavouriteSuccess,
  addSingleAvailableToFav,
  removeSingleAvailableToFav,
  removeFavouriteFailure,
  addSingleRecent,
} from "../store/auth/action";
import {
  addFavouriteDashboard,
  removeFavouriteDashboard,
} from "../services/favourite.service";
import { getSession } from "../utils/sessionstore";

export const FAVOURITE_THRESHOLD = 3;

const standardNameOf = (dashboard) =>
  (dashboard?.standarddashboardId?.standardName || "").trim().toLowerCase();

const isRecentsHidden = (dashboard) =>
  ["genieai", "genie ai", "chatbot"].includes(standardNameOf(dashboard));

export const isGenieAI = (dashboard) =>
  ["genieai", "genie ai"].includes(standardNameOf(dashboard));

// Favourites are read from state.favourites rather than filtered out of recents,
// so a starred dashboard stays listed even once it ages out of the recents window.
export function useSidebarDashboards() {
  const { favourites, recents } = useSelector((state) => ({
    favourites: state.favourites,
    recents: state.recents,
  }));

  return useMemo(() => {
    const starred = [];
    const seen = new Set();
    const keyOf = (dashboard) =>
      (dashboard?.label || "").trim().toLowerCase() || dashboard?._id;

    (favourites || []).forEach((fav) => {
      const dashboard = fav?.dashboardId;
      const key = keyOf(dashboard);
      if (!dashboard?._id || seen.has(key)) return;
      if (isRecentsHidden(dashboard)) return;
      seen.add(key);
      starred.unshift(dashboard);
    });

    const unstarred = [];
    (recents || []).forEach((rec) => {
      const dashboard = rec?.dashboardId;
      const key = keyOf(dashboard);
      if (!dashboard?._id || seen.has(key)) return;
      if (isRecentsHidden(dashboard)) return;
      seen.add(key);
      unstarred.push(dashboard);
    });

    return [...starred, ...unstarred];
  }, [favourites, recents]);
}

export function useToggleFavourite(dashboard) {
  const dispatch = useDispatch();
  const { clientID } = useParams();
  const favourites = useSelector((state) => state.favourites);
  const recents = useSelector((state) => state.recents);
  const [isBusy, setIsBusy] = useState(false);
  // The POST endpoint uses insertMany with no dedupe, so a double click would
  // create duplicate rows. Guard re-entry.
  const inFlight = useRef(false);

  const isFavourite = !!favourites?.some(
    (fav) => fav?.dashboardId?._id === dashboard?._id
  );
  // Counts only the favourites the sidebar actually lists, so a hidden one
  // (Genie AI, or a duplicate label) cannot silently consume a slot.
  const visibleFavourites = (favourites || []).filter(
    (fav) => fav?.dashboardId?._id && !isRecentsHidden(fav.dashboardId)
  ).length;
  const atCap = !isFavourite && visibleFavourites >= FAVOURITE_THRESHOLD;

  async function toggleFavourite(e) {
    if (e) e.stopPropagation();
    if (inFlight.current || !dashboard?._id) return;

    const token = getSession("session_hwai");
    const client_id = clientID ?? getSession("client_id");

    inFlight.current = true;
    setIsBusy(true);
    try {
      if (isFavourite) {
        dispatch(removeFavouriteSuccess(dashboard._id));
        // Demote into recents so the row does not vanish from under the cursor,
        // keeping its last-viewed time so it lands back in chronological order.
        const previous = recents?.find(
          (rec) => rec?.dashboardId?._id === dashboard._id
        );
        dispatch(
          addSingleRecent({
            dashboardId: dashboard,
            viewedAt: previous?.viewedAt || new Date().toISOString(),
          })
        );
        await removeFavouriteDashboard(token, dashboard._id, client_id);
        dispatch(addSingleAvailableToFav(dashboard));
      } else {
        if (atCap) return;
        const payload = {
          userId: token,
          dashboardId: dashboard,
          clientId: client_id,
        };
        dispatch(addSingleFavourite(payload));
        dispatch(removeSingleAvailableToFav(dashboard._id));
        await addFavouriteDashboard(token, payload);
      }
    } catch (err) {
      dispatch(removeFavouriteFailure(err.message));
    } finally {
      inFlight.current = false;
      setIsBusy(false);
    }
  }

  return { isFavourite, atCap, isBusy, toggleFavourite };
}
